/** @jsx jsx */

import { CSSProperties, ReactNode } from 'react';
import { jsx } from '@balance-web/core';
import { ResponsiveProp } from '@balance-web/core';
import { Box, BoxProps } from '@balance-web/box';
import { useMediaQuery } from '@balance-web/media-query';
import { forwardRefWithAs } from '@balance-web/utils';
import { BalanceTheme, useTheme } from '@balance-web/theme';

export type GridProps = {
  /** The content of this grid element. */
  children?: ReactNode;
  /** Display as an inline-level grid container. */
  inline?: boolean;
  /** Defines the columns of the grid with a space-separated list of values (e.g. 50px, 1fr, auto). */
  gridTemplateColumns?: ResponsiveProp<CSSProperties['gridTemplateColumns']>;
  /** Specifies the size of any auto-generated grid tracks (aka implicit grid tracks). */
  gridAutoColumns?: ResponsiveProp<CSSProperties['gridAutoColumns']>;
  /** Defines the rows of the grid with a space-separated list of values (e.g. 50px, 1fr, auto). */
  gridTemplateRows?: ResponsiveProp<CSSProperties['gridTemplateRows']>;
  /** Specifies the width of any auto-generated grid tracks (aka implicit grid tracks). */
  gridAutoRows?: ResponsiveProp<CSSProperties['gridAutoRows']>;
  /** Defines a grid template by referencing the names of the grid areas which are specified with the 'grid-area' property. */
  gridTemplateAreas?: ResponsiveProp<CSSProperties['gridTemplateAreas']>;
  /** A shorthand for setting grid-template-rows, grid-template-columns, and grid-template-areas in a single declaration. */
  gridTemplate?: ResponsiveProp<CSSProperties['gridTemplate']>;
  /** Defines how the auto-placement algorithm works, specifying exactly how auto-placed items get flowed into the grid. */
  gridAutoFlow?: ResponsiveProp<CSSProperties['gridAutoFlow']>;
  /** Specifies width of the gutters between the columns */
  columnGap?: ResponsiveProp<keyof BalanceTheme['spacing']>;
  /** Specifies width of the gutters between the rows */
  rowGap?: ResponsiveProp<keyof BalanceTheme['spacing']>;
  /** The size of the gap between rows and columns */
  gap?: ResponsiveProp<keyof BalanceTheme['spacing']>;
  /** Aligns the grid items (in their respective cells) along the Row/Inline axis */
  justifyItems?: ResponsiveProp<CSSProperties['justifyItems']>;
  /** Aligns the whole grid along the Row/Inline axis */
  justifyContent?: ResponsiveProp<CSSProperties['justifyContent']>;
  /** Aligns the grid items (in their respective cells) along the Column/Block axis */
  alignItems?: ResponsiveProp<CSSProperties['alignItems']>;
  /** Aligns the whole grid along the Column/Block axis */
  alignContent?: ResponsiveProp<CSSProperties['alignContent']>;
  /** Sets all of the explicit and implicit grid properties in a single declaration. */
  grid?: ResponsiveProp<CSSProperties['grid']>;
} & BoxProps;

export const Grid = forwardRefWithAs<'div', GridProps>((props, ref) => {
  const { spacing } = useTheme();
  const { mq, mapResponsiveProp } = useMediaQuery();

  const {
    alignContent = 'normal',
    alignItems = 'normal',
    columnGap,
    gap,
    grid,
    gridAutoColumns,
    gridAutoFlow,
    gridAutoRows,
    gridTemplate,
    gridTemplateAreas,
    gridTemplateColumns,
    gridTemplateRows,
    inline,
    justifyContent = 'normal',
    justifyItems = 'normal',
    rowGap,
    ...boxProps
  } = props;

  let styles = mq({
    alignContent,
    alignItems,
    display: inline ? 'inline-grid' : 'grid',
    gap: gap ? mapResponsiveProp(gap, spacing) : undefined,
    columnGap: columnGap ? mapResponsiveProp(columnGap, spacing) : undefined,
    rowGap: rowGap ? mapResponsiveProp(rowGap, spacing) : undefined,
    grid,
    gridAutoColumns,
    gridAutoFlow,
    gridAutoRows,
    gridTemplate,
    gridTemplateAreas,
    gridTemplateColumns,
    gridTemplateRows,
    justifyContent,
    justifyItems,
  });

  return <Box ref={ref} css={styles} {...boxProps} />;
});

Grid.displayName = 'Grid';
